import { getPwaUi } from "../i18n/pwaUi.js";
import type { Locale } from "../../../src/index.js";

export interface DeleteAccountModalOptions {
  locale: Locale;
  accessToken: string;
  functionUrl: string;
  anonKey: string;
  onSuccess: () => void;
  onClose: () => void;
}

const CONFIRM_WORD = "Delete";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function mountDeleteAccountModal(opts: DeleteAccountModalOptions): void {
  const ui = getPwaUi(opts.locale);

  const overlay = document.createElement("div");
  overlay.className = "delete-modal-overlay";
  overlay.innerHTML = `
    <div class="delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
      <h2 id="delete-modal-title">${esc(ui.deleteAccountModalTitle)}</h2>
      <p>${esc(ui.deleteAccountModalBodyDeleted)}</p>
      <p>${esc(ui.deleteAccountModalBodyRetained)}</p>
      <label class="delete-modal-label">
        ${esc(ui.deleteAccountModalConfirmLabel)}
        <input type="text" class="delete-modal-input" autocomplete="off" autocapitalize="off" />
      </label>
      <p class="delete-modal-error" role="alert"></p>
      <div class="delete-modal-actions">
        <button type="button" class="delete-modal-cancel">${esc(ui.deleteAccountModalCancel)}</button>
        <button type="button" class="delete-modal-submit" disabled>${esc(ui.deleteAccountModalSubmit)}</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  const input = overlay.querySelector<HTMLInputElement>(".delete-modal-input")!;
  const submit = overlay.querySelector<HTMLButtonElement>(".delete-modal-submit")!;
  const cancel = overlay.querySelector<HTMLButtonElement>(".delete-modal-cancel")!;
  const errEl = overlay.querySelector<HTMLParagraphElement>(".delete-modal-error")!;

  input.addEventListener("input", () => {
    submit.disabled = input.value !== CONFIRM_WORD;
  });

  cancel.addEventListener("click", () => {
    overlay.remove();
    opts.onClose();
  });

  submit.addEventListener("click", () => {
    void (async () => {
      submit.disabled = true;
      cancel.disabled = true;
      errEl.textContent = "";
      try {
        const res = await fetch(opts.functionUrl, {
          method: "POST",
          headers: {
            authorization: `Bearer ${opts.accessToken}`,
            apikey: opts.anonKey,
          },
        });
        if (res.status >= 200 && res.status < 300) {
          overlay.remove();
          opts.onSuccess();
          return;
        }
        errEl.textContent = ui.deleteAccountModalError;
      } catch {
        errEl.textContent = ui.deleteAccountModalError;
      } finally {
        submit.disabled = input.value !== CONFIRM_WORD;
        cancel.disabled = false;
      }
    })();
  });
}
